import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { CheckboxField, SearchField } from '@wk/components-v3-react16';
import { formatUserEmail } from '../../utils/formatUserEmail';
import { useGetUsers } from './useGetUsers';
import { useUserFilter } from './useUserFilter';
import { useUserSearch } from './useUserSearch';
import type { UserFilterModel } from './types';
import styles from './UserFilter.module.scss';

interface UserFilterProperties
  extends CustomFilterDisplayProps<UserFilterModel>,
    FilterWrapperParams {}

const UserFilter = ({ model, onStateChange, state }: UserFilterProperties) => {
  const searchInputReference = useRef<HTMLInputElement>(null);
  const selectAllInputReference = useRef<HTMLInputElement>(null);
  const { data: users, isLoading } = useGetUsers();

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        searchInputReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useUserFilter(model);

  const { filteredUsers, searchTerm, setSearchTerm } = useUserSearch(users);

  const selectedUserIds: number[] = useMemo(
    () => state.model?.userIds || [],
    [state.model?.userIds]
  );

  const isAllFilteredSelected = useMemo(
    () =>
      filteredUsers.length > 0 &&
      filteredUsers.every((user) => selectedUserIds.includes(user.userId)),
    [filteredUsers, selectedUserIds]
  );

  const isIndeterminate = useMemo(() => {
    if (filteredUsers.length === 0) {
      return false;
    }
    const selectedFilteredCount = filteredUsers.filter((user) =>
      selectedUserIds.includes(user.userId)
    ).length;
    return selectedFilteredCount > 0 && !isAllFilteredSelected;
  }, [filteredUsers, selectedUserIds, isAllFilteredSelected]);

  const selectAllAriaChecked = useMemo<boolean | 'mixed'>(() => {
    if (isIndeterminate) {
      return 'mixed';
    }
    return isAllFilteredSelected;
  }, [isIndeterminate, isAllFilteredSelected]);

  useEffect(() => {
    if (selectAllInputReference.current) {
      selectAllInputReference.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const updateModel = useCallback(
    (userIds: number[]) => {
      // Ensure unique user IDs
      const uniqueUserIds = Array.from(new Set(userIds));
      const newModel =
        uniqueUserIds.length > 0 ? { userIds: uniqueUserIds } : null;
      onStateChange({ model: newModel });
    },
    [onStateChange]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const userId = Number.parseInt(event.target.value, 10);
      const isSelected = selectedUserIds.includes(userId);
      const updatedIds = isSelected
        ? selectedUserIds.filter((id: number) => id !== userId)
        : [...selectedUserIds, userId];

      updateModel(updatedIds);
    },
    [selectedUserIds, updateModel]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllFilteredSelected) {
      const filteredIdsSet = new Set(filteredUsers.map((user) => user.userId));
      const remainingIds = selectedUserIds.filter(
        (id: number) => !filteredIdsSet.has(id)
      );
      updateModel(remainingIds);
    } else {
      const filteredIds = filteredUsers.map((user) => user.userId);
      const allIds = [...selectedUserIds, ...filteredIds];

      const uniqueIds = Array.from(new Set(allIds));
      updateModel(uniqueIds);
    }
  }, [isAllFilteredSelected, filteredUsers, selectedUserIds, updateModel]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const spaceKeys = [' ', 'Space'];
      if (
        spaceKeys.includes(event.key) &&
        event.currentTarget.getAttribute('aria-disabled')
      ) {
        event.preventDefault();
      }
    },
    []
  );

  if (isLoading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container} tabIndex={-1}>
      <div className={styles.searchWrapper}>
        <SearchField
          searchButtonId="user-search-button"
          size="large"
          hasClearIcon={searchTerm.length > 0}
          onClear={() => setSearchTerm('')}
        >
          <input
            ref={searchInputReference}
            type="search"
            name="user-search"
            aria-labelledby="user-search-button"
            autoComplete="off"
            onInput={(event) =>
              setSearchTerm((event.target as HTMLInputElement).value)
            }
            placeholder="Search users..."
            value={searchTerm}
          />
        </SearchField>
      </div>

      {filteredUsers.length > 0 && (
        <div className={styles.selectAllWrapper}>
          <CheckboxField
            labelFor="select-all-users"
            label="Select All"
            size="extra-small"
          >
            <input
              ref={selectAllInputReference}
              id="select-all-users"
              name="SelectAll"
              value="select-all"
              type="checkbox"
              checked={isAllFilteredSelected}
              aria-checked={selectAllAriaChecked}
              onChange={handleSelectAll}
              onKeyDown={handleKeydown}
              flow-id="checkbox"
            />
          </CheckboxField>
        </div>
      )}

      <div className={styles.checkboxList}>
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => {
            const isChecked = selectedUserIds.includes(user.userId);
            const inputId = `user-${user.userId}`;
            const formattedUserName = formatUserEmail(user.userEmail);
            return (
              <div key={user.userId} className={styles.checkboxWrapper}>
                <CheckboxField
                  labelFor={inputId}
                  label={formattedUserName}
                  size="extra-small"
                >
                  <input
                    id={inputId}
                    name={user.userEmail}
                    value={user.userId.toString()}
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    onKeyDown={handleKeydown}
                    flow-id="checkbox"
                  />
                </CheckboxField>
              </div>
            );
          })
        ) : (
          <div className={styles.noResults}>No matches</div>
        )}
      </div>
    </div>
  );
};

export default UserFilter;
